// Cache bust: 
import axios from 'axios'
import { handleSessionExpired, shouldHandleUnauthorized } from '../utils/sessionExpired'
import { getAuthToken } from '../utils/authStorage'

// Universal environment detection
const getEnvironmentConfig = () => {
  const isLocalhost =
    window.location.hostname.includes('localhost') ||
    window.location.hostname === '127.0.0.1'

  // Debug logging
  console.log('Environment Detection:', {
    hostname: window.location.hostname,
    protocol: window.location.protocol,
    isLocalhost,
    currentUrl: window.location.href,
  })

  if (isLocalhost) {
    // Prefer VITE_* so local UI can talk to production (www.zimark.org) when
    // localhost:8001 is not running. Falls back to local backend.
    const envApi = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim()
    const envBackend = (import.meta.env.VITE_BACKEND_URL as string | undefined)?.trim()
    const apiBaseUrl = envApi || 'http://localhost:8001/api/v1'
    const backendUrl =
      envBackend ||
      (envApi ? envApi.replace(/\/api\/v1\/?$/, '') : 'http://localhost:8001')
    const config = {
      backendUrl,
      apiBaseUrl,
      basePath: '',
      environment: 'development' as const,
    }
    console.log('Using Development Config:', config)
    return config
  }

  // All production environments use DigitalOcean
  // Use the current domain to avoid mixed content issues
  const currentDomain = window.location.hostname
  const protocol = window.location.protocol
  const config = {
    backendUrl: protocol + '//' + currentDomain,
    apiBaseUrl: protocol + '//' + currentDomain + '/api/v1',
    basePath: '',
    environment: 'production' as const,
  }
  console.log('Using Production Config:', config)
  console.log('FORCING HTTPS - Production config should use HTTPS URLs')
  return config
}

// Get current environment configuration
const envConfig = getEnvironmentConfig()

// Log environment detection for debugging
console.log('訣 Environment detected:', {
  hostname: window.location.hostname,
  environment: envConfig.environment,
  backendUrl: envConfig.backendUrl,
  apiBaseUrl: envConfig.apiBaseUrl,
  basePath: envConfig.basePath
})

// Create axios instance with environment-aware configuration
const api = axios.create({
  baseURL: envConfig.apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Debug logging for axios instance
console.log('剥 Axios Instance Created:', {
  baseURL: api.defaults.baseURL,
  timeout: api.defaults.timeout,
  headers: api.defaults.headers
})

// Request interceptor to add auth token if available
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = 'Bearer ' + token
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error)
    console.error('API Error Details:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      environment: envConfig.environment
    })
    
    // Only auto-logout for 401 errors that are NOT profile image operations or login attempts
    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      if (shouldHandleUnauthorized(url)) {
        handleSessionExpired()
      }
    }
    
    // Add retry logic for network errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.warn('Request timeout, consider retrying')
    }
    
    if (!error.response) {
      // Network error - no response received
      console.error('Network error - no response received')
    }
    
    return Promise.reject(error)
  }
)

// API endpoints
export const API_ENDPOINTS = {
  // Auth
  AUTH: '/auth',
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  ME: '/auth/me',
  LOGOUT: '/auth/logout',
  
  // Users
  USERS: '/users',
  USER_BY_ID: (id: string) => '/users/' + id,
  
  // Tutors
  TUTORS: '/tutors',
  TUTOR_BY_ID: (id: string) => '/tutors/' + id,
  
  // Students
  STUDENTS: '/students',
  STUDENT_BY_ID: (id: string) => '/students/' + id,
  
  // Bookings
  BOOKINGS: '/bookings/',
  BOOKING_BY_ID: (id: string) => '/bookings/' + id,
  
  // Sessions
  SESSIONS: '/sessions',
  SESSION_BY_ID: (id: string) => '/sessions/' + id,
  UPCOMING_SESSIONS: '/sessions/upcoming',
  
  // Conversations
  CONVERSATIONS: '/conversations/',
  CONVERSATION_BY_ID: (id: string) => '/conversations/' + id + '/',
  CONVERSATION_BY_PARTICIPANTS: (studentId: string, tutorId: string) => '/conversations/participants/' + studentId + '/' + tutorId,
  ACTIVE_CONVERSATIONS: '/conversations/active',
  CONVERSATION_STATUS: (id: string) => '/conversations/' + id + '/status',
  
  // Messages
  MESSAGES: '/messages/',
  MESSAGE_BY_ID: (id: string) => '/messages/' + id + '/',
  
  // Education
  EDUCATION: '/education',
  EDUCATION_BY_ID: (id: string) => '/education/' + id,
  
  // Experience
  EXPERIENCE: '/experience',
  EXPERIENCE_BY_ID: (id: string) => '/experience/' + id,
}

// Generic API methods
export const apiService = {
  // GET request
  get: async <T>(url: string, params?: any): Promise<T> => {
    const response = await api.get(url, { params })
    return response.data
  },

  // POST request
  post: async <T>(url: string, data?: any): Promise<T> => {
    const response = await api.post(url, data)
    return response.data
  },

  // PUT request
  put: async <T>(url: string, data?: any): Promise<T> => {
    const response = await api.put(url, data)
    return response.data
  },

  // DELETE request
  delete: async <T>(url: string, data?: any): Promise<T> => {
    const response = await api.delete(url, { data })
    return response.data
  },

  // PATCH request
  patch: async <T>(url: string, data?: any): Promise<T> => {
    const response = await api.patch(url, data)
    return response.data
  },
}

// Export environment configuration for use in other services
export const environmentConfig = envConfig

// Helper function to get full URL for images/assets
export const getFullUrl = (path: string): string => {
  if (path.startsWith('http')) {
    return path // Already a full URL
  }
  
  if (path.startsWith('/')) {
    return envConfig.backendUrl + path
  }
  
  return envConfig.backendUrl + '/' + path
}

// Helper function to get API URL
export const getApiUrl = (endpoint: string): string => {
  if (endpoint.startsWith('http')) {
    return endpoint // Already a full URL
  }
  
  if (endpoint.startsWith('/')) {
    return envConfig.apiBaseUrl + endpoint
  }
  
  return envConfig.apiBaseUrl + '/' + endpoint
}

export default api
